import { ZeroBalanceNoticeService } from '@shared';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { HomeBannersService, NavigationService, PageConfigService, PromoBannerService } from '@core';
import { BrandConfigDTO, SectionDTO } from '@models';
import { EMPTY } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SkeletonTemplateService } from '@skeleton-loader';
import { ICarouselSlide } from 'libs/ui/carousel/src/lib/carousel/interfaces/carousel-slide.interface';
import { NgTemplateOutlet } from '@angular/common';
import { CarouselComponent, PromoBannerComponent } from '@ui';
import { SectionProductsSliderComponent } from '@merch-store/app/pages/home/components/section-products-slider/section-products-slider.component';
import { SplitBannersComponent } from '@merch-store/app/pages/home/components/split-banners/split-banners.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    CarouselComponent,
    PromoBannerComponent,
    SectionProductsSliderComponent,
    SplitBannersComponent,
  ],
})
export class HomeComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly navigationService = inject(NavigationService);
  private readonly pageService = inject(PageConfigService);
  private readonly promoBannerService = inject(PromoBannerService);
  private readonly homeBannersService = inject(HomeBannersService);

  skeletonService = inject(SkeletonTemplateService);

  carouselSlides = toSignal(this.homeBannersService.getHomeCarouselSlides());
  promoSlides = toSignal(this.promoBannerService.getList());

  isHomeConfigLoading = signal<boolean>(true);
  firstSection = signal<SectionDTO | null>(null);
  restSections = signal<SectionDTO[]>([]);
  brandConfigs = signal<BrandConfigDTO[]>([]);

  constructor() {
    inject(ZeroBalanceNoticeService).showWhilePageIsOpen();
  }

  ngOnInit(): void {
    this.pageService
      .getPageConfig()
      .pipe(
        map((pageConfig) => {
          const {
            brands,
            sections: [firstSection, ...rest],
          } = pageConfig.home;
          return { brands, firstSection, restSections: rest };
        }),
        tap(({ brands, firstSection, restSections }) => {
          this.brandConfigs.set(brands);
          this.firstSection.set(firstSection);
          this.restSections.set(restSections);
          this.isHomeConfigLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public goToBrand({ link }: ICarouselSlide): void {
    this.navigationService.parseLinkAndNavigate(link);
  }
}
